import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { Popover } from "@mantine/core";
import { DropdownMenu, DropdownRow, SearchButton } from "@onengine/component-library";
import { IconChevronRight, IconDots } from "@tabler/icons-react";
import { useFileManager } from "@/components/core/FileManager";
import styles from "./FileManagerHeader.module.scss";
import { FileManagerDropdown } from "@/components/core/FileManager/components/FileManagerDropdown/FileManagerDropdown.tsx";

export function FileManagerHeader({ children }: { children?: ReactNode }) {
  return (
    <FileManagerHeaderContainer>
      <FileManagerHeaderTitle>File Manager</FileManagerHeaderTitle>
      <div className={styles.actions}>
        <FileManagerSearchInput />
        <FileManagerHeaderActions />
      </div>
      {children}
    </FileManagerHeaderContainer>
  );
}

export function FileManagerHeaderTitle({ children }: { children: ReactNode }) {
  return (
    <h3 data-slot={"file-manager-header-title"} className={styles.title}>
      <span className={clsx(styles.pathSegment, styles.pathCurrent)}>{children}</span>
    </h3>
  );
}

const OVERFLOW_DROPDOWN_STYLE = {
  border: "none",
  boxShadow: "none",
  background: "transparent",
  width: "fit-content",
};

export interface FileManagerPathAncestor {
  label: string;
  onClick: () => void;
}

export function FileManagerHeaderPath({
  rootLabel,
  ancestors = [],
}: {
  rootLabel: string;
  /** Levels the file manager sits inside (e.g. the hub's section and entity). */
  ancestors?: FileManagerPathAncestor[];
}) {
  const { breadcrumb, openFolder, navigateToBreadcrumb } = useFileManager();
  const [overflowOpened, setOverflowOpened] = useState(false);
  const pathRef = useRef<HTMLHeadingElement>(null);
  const [collapsedCount, setCollapsedCount] = useState(0);
  const [, remeasure] = useReducer((tick: number) => tick + 1, 0);

  const segments = [
    ...ancestors.map((ancestor, i) => ({
      key: `ancestor-${i}`,
      name: ancestor.label,
      navigate: ancestor.onClick,
    })),
    { key: "root", name: rootLabel, navigate: () => openFolder(null) },
    ...breadcrumb.map((entry, i) => ({
      key: entry.id,
      name: entry.name,
      navigate: () => navigateToBreadcrumb(i),
    })),
  ];

  const pathSignature = segments.map((segment) => segment.name).join("\u0000");

  useLayoutEffect(() => {
    setCollapsedCount(0);
  }, [pathSignature]);

  // Collapse one leading segment per pass until the row fits beside the actions.
  useLayoutEffect(() => {
    const element = pathRef.current;
    if (!element) return;
    if (element.scrollWidth > element.clientWidth && collapsedCount < segments.length - 1) {
      setCollapsedCount((count) => count + 1);
    }
  });

  useEffect(() => {
    const element = pathRef.current;
    if (!element) return;
    // Resetting alone can be a no-op React bails out of, leaving the row measured
    // against the old width, so force a render for the pass above to re-run.
    const observer = new ResizeObserver(() => {
      setCollapsedCount(0);
      remeasure();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const overflow = segments.slice(0, collapsedCount);
  const visible = segments.slice(collapsedCount);
  const currentKey = segments[segments.length - 1]?.key;

  return (
    <h3
      ref={pathRef}
      data-slot={"file-manager-header-title"}
      className={styles.title}
      aria-label="Folder path"
    >
      {overflow.length > 0 && (
        <Popover
          opened={overflowOpened}
          onChange={setOverflowOpened}
          position="bottom-start"
          offset={4}
          zIndex={100}
        >
          <Popover.Target>
            <button
              type="button"
              className={styles.pathOverflow}
              aria-label="Show hidden folders"
              onClick={() => setOverflowOpened((o) => !o)}
            >
              <IconDots size={16} />
            </button>
          </Popover.Target>
          <Popover.Dropdown style={OVERFLOW_DROPDOWN_STYLE} p={0}>
            <DropdownMenu width={224} scrollable={false}>
              {overflow.map((segment) => (
                <DropdownRow
                  key={segment.key}
                  title={segment.name}
                  onClickCapture={() => setOverflowOpened(false)}
                  onClick={segment.navigate}
                />
              ))}
            </DropdownMenu>
          </Popover.Dropdown>
        </Popover>
      )}
      {visible.map((segment, i) => (
        <span
          key={segment.key}
          className={clsx(styles.pathEntry, visible.length === 1 && styles.pathSole)}
        >
          {(i > 0 || overflow.length > 0) && (
            <IconChevronRight size={16} className={styles.pathSeparator} />
          )}
          <button
            type="button"
            className={clsx(styles.pathSegment, segment.key === currentKey && styles.pathCurrent)}
            onClick={segment.navigate}
            disabled={segment.key === currentKey}
          >
            {segment.name}
          </button>
        </span>
      ))}
    </h3>
  );
}

interface FileManagerSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder?: string;
}

/**
 * Collapses to SearchButton's icon variant while the header is compact, and
 * expands back to a field on click. A search carrying a term stays expanded, so
 * an active filter is never hidden behind an icon.
 */
export function FileManagerSearchField({
  value,
  onChange,
  onClear,
  placeholder = "Search...",
}: FileManagerSearchFieldProps) {
  const isCompact = useFileManagerHeaderCompact();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasTerm = value.trim().length > 0;

  if (isCompact && !isExpanded && !hasTerm) {
    return (
      <div data-slot={"file-manager-search-input"} className={styles.searchCollapsed}>
        <SearchButton
          variant="icon"
          onClick={() => setIsExpanded(true)}
          className={styles.searchTrigger}
        />
      </div>
    );
  }

  return (
    <div data-slot={"file-manager-search-input"}>
      <SearchButton
        className={styles.searchField}
        variant="field"
        active={isCompact || hasTerm}
        autoFocus={isCompact}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onClear={() => {
          onClear?.();
          if (isCompact) setIsExpanded(false);
        }}
        onBlur={() => {
          if (isCompact && !hasTerm) setIsExpanded(false);
        }}
      />
    </div>
  );
}

export function FileManagerSearchInput() {
  const { searchInputValue, onSearch, onClearSearch } = useFileManager();

  return (
    <FileManagerSearchField value={searchInputValue} onChange={onSearch} onClear={onClearSearch} />
  );
}

/**
 * Below this the row cannot hold the search, both button labels and a usable
 * path. Measured on the header itself, not the viewport: the toolbar's width is
 * whatever the app shell and the hub's sidebar leave behind, so a wide window
 * can still produce a cramped header.
 */
const COMPACT_HEADER_WIDTH = 600;

const HeaderCompactContext = createContext(false);

/** True when the header is too narrow for controls to carry their labels. */
export const useFileManagerHeaderCompact = () => useContext(HeaderCompactContext);

export function FileManagerHeaderContainer({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setIsCompact(entry.contentRect.width < COMPACT_HEADER_WIDTH);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <HeaderCompactContext.Provider value={isCompact}>
      <div
        ref={containerRef}
        data-slot={"file-manager-header-container"}
        className={styles.container}
      >
        {children}
      </div>
    </HeaderCompactContext.Provider>
  );
}

export function FileManagerHeaderActions() {
  return <FileManagerDropdown />;
}

export function FileManagerBreadcrumb() {
  const { breadcrumb, openFolder, navigateToBreadcrumb } = useFileManager();

  return (
    <nav className={styles.breadcrumb} aria-label="Folder navigation">
      <button type="button" className={styles.breadcrumbItem} onClick={() => openFolder(null)}>
        Root
      </button>
      {breadcrumb.map((entry, i) => (
        <span key={entry.id} className={styles.breadcrumbEntry}>
          <IconChevronRight size={14} className={styles.breadcrumbSeparator} />
          <button
            type="button"
            className={styles.breadcrumbItem}
            onClick={() => navigateToBreadcrumb(i)}
          >
            {entry.name}
          </button>
        </span>
      ))}
    </nav>
  );
}
